/**
 * MAYALUXXE 50% deposit purchase option.
 * Adds the matching deposit product variant to Shopify cart and sends the
 * customer to standard Shopify checkout. Does NOT change original item price,
 * intercept checkout or claim a partial payment on the original order.
 */
(() => {
  if (customElements.get('mayaluxxe-payment-selector')) return;

  // Shopify treats deposits and full-price products as separate cart items.
  // Remove ONLY the opposite payment option for this same original product.
  // Everything else in the customer's cart is preserved.
  async function removeConflictingCartLines(oppositeVariantIds) {
    const ids = new Set(oppositeVariantIds.map((id) => String(id)));
    const root = window.Shopify?.routes?.root || '/';
    const result = await fetch(root + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!result.ok) throw new Error('Unable to check your cart. Please try again.');
    const cart = await result.json();
    const conflicts = cart.items.filter((item) => ids.has(String(item.variant_id)));
    if (!conflicts.length) return false;

    const updates = {};
    for (const item of conflicts) updates[item.key] = 0;
    const update = await fetch(root + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (!update.ok) throw new Error('Unable to switch payment methods. Please try again.');
    const updatedCart = await update.json();
    if (updatedCart.items.some((item) => ids.has(String(item.variant_id)))) {
      throw new Error('Unable to remove the previous payment option. Please try again.');
    }
    return true;
  }

  // Called by the existing full-price form BEFORE its regular add-to-cart fetch.
  // Narrowly scoped: doesn't alter products lacking a deposit selector.
  window.mayaluxxeBeforeFullPriceAdd = async function (productForm) {
    const selector = productForm.querySelector('mayaluxxe-payment-selector');
    if (!selector || !selector.depositVariants) return;
    await removeConflictingCartLines(selector.depositVariants.map((variant) => variant.id));
  };

  class MayaluxxePaymentSelector extends HTMLElement {
    connectedCallback() {
      this.formComponent = this.closest('product-form-component');
      this.form = this.closest('form');
      this.button = this.form?.querySelector('[data-mx-deposit-checkout]');
      if (!this.formComponent || !this.form || !this.button) return;

      try {
        this.fullVariants = JSON.parse(this.querySelector('[data-mx-full-variants]').textContent);
        this.depositVariants = JSON.parse(this.querySelector('[data-mx-deposit-variants]').textContent);
      } catch (_) {
        this.showError('Deposit options are not configured correctly. Please contact the store.');
        return;
      }

      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      this.addEventListener('change', () => this.refresh(), { signal });
      this.button.addEventListener('click', (event) => this.payDeposit(event), { signal });
      // This theme updates its hidden variant ID after an async product:select event.
      const section = this.closest('.shopify-section');
      section?.addEventListener('shopify:product:select', (event) => {
        if (!this.isConnected || !event.target?.closest?.('.shopify-section')?.contains(this)) return;
        Promise.resolve(event.promise).then(() => requestAnimationFrame(() => this.refresh())).catch(() => requestAnimationFrame(() => this.refresh()));
      }, { signal });
      this.refresh();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      document.documentElement.classList.remove('mx-deposit-active');
    }

    getCurrentVariant() {
      const input = this.form?.querySelector('input[name="id"]');
      return this.fullVariants?.find((variant) => String(variant.id) === String(input?.value));
    }

    getDepositVariant(fullVariant) {
      if (!fullVariant) return null;
      return this.depositVariants.find((depositVariant) =>
        Array.isArray(depositVariant.options) &&
        depositVariant.options.length === fullVariant.options?.length &&
        depositVariant.options.every((value, index) => value === fullVariant.options[index])
      );
    }

    money(cents) {
      const number = (Number(cents) || 0) / 100;
      return 'R' + number.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    showError(message) {
      const container = this.querySelector('[data-mx-error]');
      if (container) {
        container.textContent = message;
        container.hidden = !message;
      }
    }

    refresh() {
      if (!this.formComponent || !this.button) return;
      const mode = this.querySelector('input[type="radio"]:checked')?.value || 'full';
      const fullVariant = this.getCurrentVariant();
      const depositVariant = this.getDepositVariant(fullVariant);
      const fullCents = fullVariant?.price;
      const depositCents = depositVariant?.price;
      const correctlyPriced = fullVariant && depositVariant && Number(depositCents) === Math.ceil(Number(fullCents) / 2);
      const enabled = correctlyPriced && depositVariant.available && fullVariant.available;

      this.formComponent.classList.toggle('mx-payment-deposit', mode === 'deposit');
      document.documentElement.classList.toggle('mx-deposit-active', mode === 'deposit');
      this.button.hidden = mode !== 'deposit';
      this.button.disabled = !enabled || this.busy;
      this.querySelector('[data-mx-full-amount]').textContent = fullVariant ? this.money(fullCents) : '—';
      this.querySelector('[data-mx-deposit-amount]').textContent = depositVariant ? this.money(depositCents) : '—';
      this.querySelector('[data-mx-balance-amount]').textContent = correctlyPriced ? this.money(fullCents - depositCents) : '—';
      this.querySelector('[data-mx-note]').textContent = mode === 'deposit'
        ? 'Your remaining 50% will be invoiced when your order is ready. Delivery is charged separately. Estimated turnaround: approximately 2 weeks.'
        : 'Pre-order item. Estimated turnaround: approximately 2 weeks.';

      if (mode === 'deposit' && !correctlyPriced) {
        this.showError('This size/colour is not set up for a 50% deposit. Please choose another option or contact us.');
      } else if (mode === 'deposit' && !enabled) {
        this.showError('This size/colour is currently unavailable for deposit orders.');
      } else {
        this.showError('');
      }
    }

    async payDeposit(event) {
      event.preventDefault();
      if (this.busy) return;
      const fullVariant = this.getCurrentVariant();
      const depositVariant = this.getDepositVariant(fullVariant);
      if (!fullVariant || !depositVariant || !fullVariant.available || !depositVariant.available ||
          Number(depositVariant.price) !== Math.ceil(Number(fullVariant.price) / 2)) {
        this.refresh();
        return;
      }

      const qty = Number(this.form.querySelector('input[name="quantity"]')?.value || 1);
      if (!Number.isInteger(qty) || qty < 1 || !this.form.checkValidity()) {
        this.showError('Please choose a valid quantity and all required options.');
        return;
      }

      this.busy = true;
      this.button.textContent = 'Adding deposit…';
      this.refresh();
      const remaining = Number(fullVariant.price) - Number(depositVariant.price);
      const root = window.Shopify?.routes?.root || '/';
      try {
        // An abandoned checkout may leave the full-price dress in the cart.
        // Remove that version before adding the half-price deposit variant.
        await removeConflictingCartLines(this.fullVariants.map((variant) => variant.id));
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: Number(depositVariant.id),
            quantity: qty,
            properties: {
              'Payment option': '50% deposit',
              'Original product': this.dataset.productTitle,
              'Original variant': fullVariant.title,
              'Original price per item': this.money(fullVariant.price),
              'Remaining balance per item': this.money(remaining),
              'Balance payment': 'Invoice sent when order is ready'
            }
          })
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Unable to add the deposit item.');
        }
        // Checkout uses the Shopify cart and configured payment providers.
        window.location.assign(root + 'checkout');
      } catch (error) {
        this.showError(error.message || 'Something went wrong. Please try again.');
        this.busy = false;
        this.button.textContent = 'Pay 50% deposit';
        this.refresh();
      }
    }
  }

  customElements.define('mayaluxxe-payment-selector', MayaluxxePaymentSelector);
})();
